/**
 * The hosted router.
 *
 * This replaces exactly one function - `Assistant.router` - and nothing else.
 * That boundary is the whole point, and it is worth being explicit about why,
 * because it is tempting to hand a language model more of this pipeline than it
 * should have.
 *
 * The model is given the intent catalogue and the student's question, and it
 * returns THREE things, none of which is prose: which experiment to run, any
 * knob values the student named, and - when nothing fits - which experiment
 * comes closest. It never sees a number the solver produced, never writes a
 * finding, and never appears in the report. Every sentence the student reads is
 * still assembled from measured output by the intent's own interpreter. So the
 * failure mode of a bad model call is "it ran the uniformity experiment when I
 * asked about selectivity" - visible, recoverable, and a worse experience but
 * not a false one. The failure mode we are refusing is a model that explains
 * physics the simulation does not do.
 *
 * Four things follow from that:
 *
 *   - A returned id is validated against the registered intents. Anything else
 *     is treated as no match. The same is true of a returned `nearest`.
 *   - Returned params are clamped to the ranges the intent itself declared, and
 *     any key it did not declare is dropped. A model cannot widen a sweep past
 *     what the physics allows, because the range is the physical limit.
 *   - The model is told it may answer `null`, so the honest "I cannot turn that
 *     into an experiment" response survives.
 *   - Every failure - offline, timeout, missing key, provider outage, garbage
 *     JSON - falls back to `routeLocally`. A classroom with no network gets the
 *     pattern router and never gets an error.
 */

import { routeLocally } from '../Assistant'
import type { Routing } from '../Assistant'
import { coerceParams, NO_PARAMS, scanParams } from '../params'
import { storedProvider } from './provider'
import type { BaseIntentContext, Intent } from '../types'
import { ROUTER_ENDPOINT, ROUTER_TIMEOUT_MS, type ProviderId, type Region } from './config'
import { currentRegion, guessRegion, storedRegion } from './region'

/** What the proxy reports about the call it made, for the UI to show. */
export interface RouteMeta {
  provider: ProviderId
  model: string
  region: Region
  /** True when the pattern router answered instead. */
  local: boolean
}

let lastMeta: RouteMeta | null = null

/** Who answered the most recent question. Null until one has been asked. */
export const lastRouteMeta = (): RouteMeta | null => lastMeta

/** What the proxy decided, in a form that survives the cache. */
interface Decision {
  id: string | null
  /** Already clamped to the intent's declared ranges. */
  params: Record<string, number>
  /** Only meaningful when `id` is null. */
  nearest: string | null
}

/**
 * Same question, same answer. Routing is deterministic from the student's
 * point of view, and a class of thirty asking the four suggested questions
 * should not be thirty times the token spend. Per-session only.
 *
 * Params are cached with the id rather than recomputed, which is correct
 * because they are derived from the same question the key is built from - the
 * whole decision is a function of the text.
 */
const cache = new Map<string, Decision>()

const cacheKey = (question: string, ids: string[]) =>
  `${ids.join(',')}|${question.trim().toLowerCase()}`

export function createLlmRouter<C extends BaseIntentContext>(
  intents: Intent<C>[],
): (question: string) => Promise<Routing<C>> {
  const byId = new Map(intents.map((i) => [i.id, i]))
  const ids = intents.map((i) => i.id)

  /**
   * The catalogue the model chooses from: ids and prose the intents already
   * carry, plus the knobs each one will accept. The ranges go over the wire so
   * the model knows what it is allowed to fill in - but they are enforced here
   * on the way back regardless, because a prompt is guidance and a clamp is not.
   */
  const catalogue = intents.map((i) => ({
    id: i.id,
    example: i.example,
    summary: i.summary,
    params: i.params?.map((p) => ({
      id: p.id,
      describe: p.describe,
      unit: p.unit,
      min: p.min,
      max: p.max,
    })),
  }))

  /** Turn a validated decision into the routing the assistant runs on. */
  const toRouting = (d: Decision): Routing<C> => {
    const intent = d.id ? byId.get(d.id) : null
    if (intent) return { kind: 'run', intent, params: d.params }
    const near = d.nearest ? byId.get(d.nearest) : null
    return near ? { kind: 'nearest', intent: near } : { kind: 'none' }
  }

  return async (question: string): Promise<Routing<C>> => {
    const local = (): Routing<C> => {
      lastMeta = { provider: 'openai', model: 'pattern router', region: currentRegion(), local: true }
      return routeLocally(intents, question)
    }

    const key = cacheKey(question, ids)
    const hit = cache.get(key)
    if (hit) return toRouting(hit)

    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), ROUTER_TIMEOUT_MS)
    try {
      const res = await fetch(ROUTER_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        signal: abort.signal,
        body: JSON.stringify({
          question,
          intents: catalogue,
          // A named provider is the strongest thing a student can say and the
          // proxy honours it over everything below. Null when they have not
          // chosen, which is the common case.
          provider: storedProvider(),
          // Sent as two separate facts rather than one answer, because the
          // proxy has to be able to tell them apart: an explicit choice
          // outranks what the edge saw, and the edge outranks a guess.
          override: storedRegion(),
          guess: guessRegion(),
        }),
      })
      if (!res.ok) return local()

      const data = (await res.json()) as {
        id?: string | null
        params?: unknown
        nearest?: string | null
        provider?: ProviderId
        model?: string
        region?: Region
      }

      // The trust management, in three lines. An id the model invented is not
      // an intent and is treated as no match rather than as an error; the same
      // goes for a nearest it invented; and a param outside the range its own
      // intent declared is clamped to that range on the way in.
      const id = data.id && byId.has(data.id) ? data.id : null
      const intent = id ? byId.get(id) : null
      const nearest = !id && data.nearest && byId.has(data.nearest) ? data.nearest : null

      // Both readers run, and the model wins where they disagree. The scanner
      // catches a plain "40 mTorr" the model overlooked; the model catches
      // "turn the bias down to 300" and everything else that needs the sentence
      // read rather than the digits found.
      const params = intent
        ? { ...scanParams(intent.params, question), ...coerceParams(intent.params, data.params) }
        : NO_PARAMS

      const decision: Decision = { id, params: { ...params }, nearest }
      cache.set(key, decision)
      lastMeta = {
        provider: data.provider ?? 'openai',
        model: data.model ?? 'unknown',
        region: data.region ?? currentRegion(),
        local: false,
      }
      return toRouting(decision)
    } catch {
      // Offline, timed out, or the proxy is not deployed. The classroom keeps
      // working - this is why the pattern router stays in the build.
      return local()
    } finally {
      clearTimeout(timer)
    }
  }
}
